import { useEffect, useMemo, useState } from 'react';
import { Title, useNotify, useTranslate } from 'react-admin';
import * as echarts from 'echarts';
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';

import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

import { API_URL, httpClient } from '../../../httpClient';
import {
  ChartCard,
  DashboardHeader,
  EChartsComponent,
  StatCard,
  type AgingBucket,
  type OperationsDashboardData,
} from './shared';

// Extends the shared OperationsDashboardData with the time-to-first-contact
// KPI and uncontacted-queue fields added by this dashboard's backend handler.
// (The shared interface is owned elsewhere; we layer the new fields here.)
interface OperationsDashboardDataWithContact extends OperationsDashboardData {
  avgDaysToFirstContact: number | null;
  medianDaysToFirstContact: number | null;
  p90DaysToFirstContact: number | null;
  uncontactedAging: AgingBucket[];
}

const BUCKET_ORDER = ['0-3d', '4-7d', '8-15d', '15d+'];

const UNCONTACTED_BUCKET_ORDER = ['0-1d', '2-3d', '4-7d', '7d+'];

const uncontactedBucketColor = (bucket: string): string => {
  switch (bucket) {
    case '0-1d':
      return '#22c55e';
    case '2-3d':
      return '#eab308';
    case '4-7d':
      return '#f97316';
    case '7d+':
      return '#ef4444';
    default:
      return '#6366f1';
  }
};

const bucketColor = (bucket: string): string => {
  switch (bucket) {
    case '0-3d':
      return '#22c55e';
    case '4-7d':
      return '#eab308';
    case '8-15d':
      return '#f97316';
    case '15d+':
      return '#ef4444';
    default:
      return '#6366f1';
  }
};

export const OperationsDashboard = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const notify = useNotify();

  const [data, setData] =
    useState<OperationsDashboardDataWithContact | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Guard against a stale response overwriting a newer one on remount.
    let cancelled = false;
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const { json } = (await httpClient(
          `${API_URL}/job-application/dashboard/operations`,
          { method: 'GET' },
        )) as { json: { data: OperationsDashboardDataWithContact } };

        if (!cancelled) setData(json.data);
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching operations dashboard:', error);
        notify('ra.notification.http_error', { type: 'error' });
        setData(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [notify]);

  const aging = data?.aging ?? [];
  const stale = data?.staleApplications ?? [];
  const uncontacted = data?.uncontactedAging ?? [];

  const avgContact = data?.avgDaysToFirstContact;
  const avgContactDisplay =
    avgContact === null || avgContact === undefined
      ? '—'
      : translate('resources.job-application-dashboard.dashboards.operations.daysValue', {
          days: avgContact,
        });

  const uncontactedTotal = uncontacted.reduce((sum, b) => sum + b.count, 0);

  const uncontactedChartOption = useMemo(() => {
    const ordered = UNCONTACTED_BUCKET_ORDER.map((bucket) => {
      const b = uncontacted.find((x) => x.bucket === bucket);
      return { bucket, count: b ? b.count : 0 };
    });
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: 8, right: 16, top: 16, bottom: 8, containLabel: true },
      xAxis: { type: 'category', data: ordered.map((o) => o.bucket) },
      yAxis: { type: 'value', minInterval: 1 },
      series: [
        {
          type: 'bar',
          data: ordered.map((o) => ({
            value: o.count,
            itemStyle: { color: uncontactedBucketColor(o.bucket) },
          })),
          barMaxWidth: 64,
          label: { show: true, position: 'top' },
        },
      ],
    } as echarts.EChartsOption;
  }, [uncontacted]);

  return (
    <Box sx={{ p: 3 }}>
      <Title title={translate('resources.job-application-dashboard.dashboards.tabOperations')} />
      <DashboardHeader
        title={translate('resources.job-application-dashboard.dashboards.tabOperations')}
        dataSinceAt={data?.dataSinceAt}
      />

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title={translate(
              'resources.job-application-dashboard.dashboards.operations.avgDaysToFirstContact',
            )}
            value={avgContactDisplay}
            icon={HourglassEmptyIcon}
            color="#6366f1"
            loading={isLoading}
            tooltip={translate(
              'resources.job-application-dashboard.dashboards.operations.tooltips.avgDaysToFirstContact',
            )}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <ChartCard
            title={translate(
              'resources.job-application-dashboard.dashboards.operations.uncontactedQueue',
            )}
            tooltip={translate(
              'resources.job-application-dashboard.dashboards.operations.tooltips.uncontactedQueue',
            )}
          >
            {!isLoading && uncontactedTotal === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 2, textAlign: 'center' }}
              >
                {translate('resources.job-application-dashboard.charts.noData')}
              </Typography>
            ) : (
              <EChartsComponent
                option={uncontactedChartOption}
                height={280}
                loading={isLoading}
              />
            )}
          </ChartCard>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <ChartCard
            title={translate('resources.job-application-dashboard.dashboards.operations.aging')}
            tooltip={translate('resources.job-application-dashboard.dashboards.operations.tooltips.aging')}
          >
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {translate('resources.job-application-dashboard.dashboards.operations.statusName')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {translate('resources.job-application-dashboard.dashboards.operations.count')}
                    </TableCell>
                    {BUCKET_ORDER.map((b) => (
                      <TableCell key={b} align="right" sx={{ fontWeight: 600 }}>
                        {b}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading || aging.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2 + BUCKET_ORDER.length} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          {isLoading
                            ? translate('ra.page.loading')
                            : translate('resources.job-application-dashboard.charts.noData')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    aging.map((row) => (
                      <TableRow key={row.statusId} hover>
                        <TableCell>{row.statusName}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {row.total}
                        </TableCell>
                        {BUCKET_ORDER.map((bucket) => {
                          const b = row.buckets.find((x) => x.bucket === bucket);
                          const count = b ? b.count : 0;
                          const color = bucketColor(bucket);
                          return (
                            <TableCell
                              key={bucket}
                              align="right"
                              sx={{
                                color: count > 0 ? color : theme.palette.text.disabled,
                                fontWeight: count > 0 ? 600 : 400,
                                backgroundColor: count > 0 ? alpha(color, 0.06) : 'transparent',
                              }}
                            >
                              {count}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </ChartCard>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <ChartCard
            title={translate('resources.job-application-dashboard.dashboards.operations.staleApplications')}
            tooltip={translate('resources.job-application-dashboard.dashboards.operations.tooltips.staleApplications')}
          >
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {translate('resources.job-application-dashboard.dashboards.operations.candidateName')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {translate('resources.job-application-dashboard.dashboards.operations.statusName')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {translate('resources.job-application-dashboard.dashboards.operations.daysInStatus')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading || stale.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          {isLoading
                            ? translate('ra.page.loading')
                            : translate('resources.job-application-dashboard.charts.noData')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    stale.map((row) => (
                      <TableRow key={row.jobApplicationId} hover>
                        <TableCell>{row.candidateName}</TableCell>
                        <TableCell>{row.statusName}</TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: row.daysInStatus > 15 ? '#ef4444' : row.daysInStatus > 7 ? '#f97316' : 'inherit',
                            fontWeight: row.daysInStatus > 7 ? 600 : 400,
                          }}
                        >
                          {row.daysInStatus}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};
